import { useState, useEffect } from 'react';
import * as unifiedApi from '../services/unifiedApi';

// Generic hook for data fetching with loading and error states
export function useApiData<T>(
  fetchFunction: () => Promise<T>,
  initialData: T,
  refreshInterval?: number
) {
  const [data, setData] = useState<T>(initialData);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Function to fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      const result = await fetchFunction();
      setData(result);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('An unknown error occurred'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchData();

    // Set up polling if refreshInterval is provided
    let intervalId: number | undefined;
    if (refreshInterval && refreshInterval > 0) {
      intervalId = window.setInterval(() => {
        fetchData();
      }, refreshInterval);
    }

    // Clean up interval on unmount
    return () => {
      if (intervalId !== undefined) {
        clearInterval(intervalId);
      }
    };
  }, [refreshInterval]); // Re-run if refreshInterval changes

  // Function to manually refresh data
  const refresh = () => {
    fetchData();
  };

  return { data, loading, error, lastUpdated, refresh };
}

// Specific hooks for different API endpoints
export function useWaterQualityData(refreshInterval?: number) {
  return useApiData(
    unifiedApi.getWaterQuality,
    { ph: 0, ec: 0, temperature: 0, dissolvedOxygen: 0 },
    refreshInterval
  );
}

export function useSaltsData() {
  return useApiData(unifiedApi.getSalts, []);
}

export function useFormulationsData() {
  return useApiData(unifiedApi.getFormulations, []);
}

// For time-series data (e.g., sensor readings over time)
export function useTimeSeriesData(metric: string, interval: number = 0) {
  const fetchTimeSeriesData = async () => {
    // This would be a real API call in a production app
    // For now, we'll simulate it with mock data
    const now = new Date();
    const data = [];

    // Create mock time series data for the past 24 hours
    for (let i = 24; i >= 0; i--) {
      const time = new Date(now.getTime() - i * 60 * 60 * 1000); // hourly data
      const value = metric === 'ph'
        ? 5.5 + Math.random() * 2 // pH range 5.5-7.5
        : metric === 'ec'
        ? 1.2 + Math.random() * 0.8 // EC range 1.2-2.0
        : metric === 'temperature'
        ? 20 + Math.random() * 5 // Temperature range 20-25°C
        : 6 + Math.random() * 2; // DO range 6-8 mg/L

      data.push({
        name: time.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
        value: parseFloat(value.toFixed(2)),
        timestamp: time.toISOString()
      });
    }

    return data;
  };

  return useApiData(fetchTimeSeriesData, [], interval);
}

// Real-time water quality monitoring hook
export function useRealTimeWaterQuality(refreshInterval: number = 5000) {
  const fetchRealTimeData = async () => {
    // Simulate real-time sensor data with realistic variations
    const baseValues = {
      ph: 6.2,
      ec: 1.4,
      temperature: 22,
      dissolvedOxygen: 7.2,
      tds: 980
    };

    // Add realistic variations
    const variations = {
      ph: (Math.random() - 0.5) * 0.2, // ±0.1 pH units
      ec: (Math.random() - 0.5) * 0.1, // ±0.05 mS/cm
      temperature: (Math.random() - 0.5) * 1.0, // ±0.5°C
      dissolvedOxygen: (Math.random() - 0.5) * 0.4, // ±0.2 mg/L
      tds: (Math.random() - 0.5) * 50 // ±25 ppm
    };

    return {
      ph: parseFloat((baseValues.ph + variations.ph).toFixed(2)),
      ec: parseFloat((baseValues.ec + variations.ec).toFixed(2)),
      temperature: parseFloat((baseValues.temperature + variations.temperature).toFixed(1)),
      dissolvedOxygen: parseFloat((baseValues.dissolvedOxygen + variations.dissolvedOxygen).toFixed(1)),
      tds: Math.round(baseValues.tds + variations.tds),
      timestamp: new Date().toISOString(),
      status: 'online'
    };
  };

  return useApiData(fetchRealTimeData, {
    ph: 0,
    ec: 0,
    temperature: 0,
    dissolvedOxygen: 0,
    tds: 0,
    timestamp: '',
    status: 'offline'
  }, refreshInterval);
}

// pH adjustment calculation hook
export function usePHAdjustmentCalculation() {
  const [isCalculating, setIsCalculating] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const calculatePHAdjustment = async (params: {
    waterVolume: number;
    initialPh: number;
    targetPh: number;
    alkalinity: number;
    productId?: string;
  }) => {
    setIsCalculating(true);
    setError(null);

    try {
      const response = await fetch('/api/ph/calculate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setResult(data.data);
      return data.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setIsCalculating(false);
    }
  };

  return {
    calculatePHAdjustment,
    isCalculating,
    result,
    error,
    clearResult: () => setResult(null),
    clearError: () => setError(null)
  };
}

export default {
  useWaterQualityData,
  useSaltsData,
  useFormulationsData,
  useTimeSeriesData,
  useRealTimeWaterQuality,
  usePHAdjustmentCalculation
};
